// bluaIQ — Centralized plan catalog (single source of truth)
//
// PR-0 of the pricing/tier migration: one typed catalog, shared by the
// FRONTEND (pricing cards, Plan & Billing, ProFeatureGate) and the BACKEND
// (PLAN_LIMITS, FAIR_USE_LIMITS, effective-plan ranking). Existing constants
// now DERIVE from this file, so a future price change / hide-or-retire of a
// plan / visibility change is a one-place edit instead of a multi-component
// rewrite.
//
// IMPORTANT — this PR is behavior-preserving:
//   * Values below are EXACTLY the current ones (STARTER/PRO/BUILDER,
//     $39/$89/$209, current limits / fair-use / ranks).
//   * Display labels are NOT changed (the UI still renders the plan KEY).
//   * Stripe price IDs are NOT stored here — only the NAME of the env var that
//     holds each id (ids are per-environment secrets, set in Railway). Checkout
//     is NOT rewired in PR-0.
//   * Founding / Growth / Enterprise are NOT added here yet.
//
// Pure data + pure functions. No I/O, no env reads, no browser/node-only APIs —
// safe to import from both `src/components/*` and `src/server/*`.

// Stable internal plan keys. `as const` so the literal union is preserved for
// every downstream `PlanTier`/`Plan` type that derives from it.
export const PLAN_KEYS = ['STARTER', 'PRO', 'BUILDER'] as const
export type PlanKey = (typeof PLAN_KEYS)[number]

export type PlanStatus = 'active' | 'hidden' | 'retired'
export type FeatureTier = 'founding' | 'starter' | 'pro' | 'growth' | 'enterprise'
export type ExportMode = 'optimized' | 'full'

export interface Bilingual {
  en: string
  es: string
}

export interface PlanConfig {
  key: PlanKey
  /** Marketing name (future-facing; the current UI still renders `key`). */
  displayName: Bilingual
  /** Optional public label if it must differ from displayName/key. */
  publicLabel?: Bilingual
  badge?: Bilingual
  shortDesc: Bilingual
  /** Display price (cards). Distinct from the Stripe-charged amount. null = contact-only. */
  publicPriceMonthly: number | null
  publicPriceYearly?: number | null
  /** NAME of the env var holding the Stripe price id (never the id itself). */
  billingPriceIdMonthlyEnv?: string
  billingPriceIdYearlyEnv?: string
  // ── Enforcement (current behavior) ──
  projectLimit: number
  userLimit: number
  storageGB: number
  extraProjectPrice: number
  /** Exposed AI-call number (20) or -1 = unlimited-with-fair-use. Display-only today. */
  aiCallsPerMonth: number
  aiSoft: number
  aiHard: number
  /** Daily-Log export caps (Infinity = uncapped). */
  dleExportsPerMonth: number
  dleModes: readonly ExportMode[]
  // ── Catalog / visibility metadata (for future migration) ──
  featureTier: FeatureTier
  isPublic: boolean
  isCheckoutEnabled: boolean
  isInviteOnly: boolean
  isContactOnly: boolean
  status: PlanStatus
  sortOrder: number
  recommended: boolean
  /** i18n key for the per-plan AI feature label on the landing pricing card. */
  landingAiLabelKey: string
  /** Plan-key aliases that should resolve to this plan (future use). */
  legacyAliases?: readonly string[]
}

// Current plans — values are intentionally identical to today's literals.
export const PLAN_CATALOG: readonly PlanConfig[] = [
  {
    key: 'STARTER',
    displayName: { en: 'Starter', es: 'Starter' },
    shortDesc: {
      en: 'For small builders leaving scattered spreadsheets behind.',
      es: 'Para constructores pequeños que dejan atrás las hojas dispersas.',
    },
    publicPriceMonthly: 199,
    publicPriceYearly: 1990,
    billingPriceIdMonthlyEnv: 'STRIPE_PRICE_STARTER_MONTHLY',
    billingPriceIdYearlyEnv: 'STRIPE_PRICE_STARTER_YEARLY',
    projectLimit: 3,
    userLimit: 2,
    storageGB: 3,
    extraProjectPrice: 12,
    aiCallsPerMonth: 20,
    aiSoft: 100,
    aiHard: 300,
    dleExportsPerMonth: 10,
    dleModes: ['optimized'],
    featureTier: 'starter',
    isPublic: true,
    isCheckoutEnabled: true,
    isInviteOnly: false,
    isContactOnly: false,
    status: 'active',
    sortOrder: 0,
    recommended: false,
    landingAiLabelKey: 'landing.pricing.feature.aiStarter',
    legacyAliases: ['SOLO'],
  },
  {
    key: 'PRO',
    displayName: { en: 'Pro', es: 'Pro' },
    badge: { en: 'Most popular', es: 'Más popular' },
    shortDesc: {
      en: 'For active residential builders and small developers.',
      es: 'Para constructores residenciales activos y pequeños desarrolladores.',
    },
    publicPriceMonthly: 299,
    publicPriceYearly: 2990,
    billingPriceIdMonthlyEnv: 'STRIPE_PRICE_PRO_MONTHLY',
    billingPriceIdYearlyEnv: 'STRIPE_PRICE_PRO_YEARLY',
    projectLimit: 10,
    userLimit: 5,
    storageGB: 15,
    extraProjectPrice: 12,
    aiCallsPerMonth: -1,
    aiSoft: 300,
    aiHard: 900,
    dleExportsPerMonth: Infinity,
    dleModes: ['optimized', 'full'],
    featureTier: 'pro',
    isPublic: true,
    isCheckoutEnabled: true,
    isInviteOnly: false,
    isContactOnly: false,
    status: 'active',
    sortOrder: 1,
    recommended: true,
    landingAiLabelKey: 'landing.pricing.feature.aiPro',
    legacyAliases: ['TEAM'],
  },
  {
    key: 'BUILDER',
    // Public label is "Growth" (pricing cutover); the internal key stays
    // BUILDER so plan gating / Stripe env mapping are unchanged.
    displayName: { en: 'Growth', es: 'Growth' },
    shortDesc: {
      en: 'For serious / spec-home builders and small developers.',
      es: 'Para constructores serios / de casas spec y pequeños desarrolladores.',
    },
    publicPriceMonthly: 499,
    publicPriceYearly: 4990,
    billingPriceIdMonthlyEnv: 'STRIPE_PRICE_BUILDER_MONTHLY',
    billingPriceIdYearlyEnv: 'STRIPE_PRICE_BUILDER_YEARLY',
    projectLimit: 20,
    userLimit: 10,
    storageGB: 50,
    extraProjectPrice: 12,
    aiCallsPerMonth: -1,
    aiSoft: 1000,
    aiHard: 3000,
    dleExportsPerMonth: Infinity,
    dleModes: ['optimized', 'full'],
    featureTier: 'growth',
    isPublic: true,
    isCheckoutEnabled: true,
    isInviteOnly: false,
    isContactOnly: false,
    status: 'active',
    sortOrder: 2,
    recommended: false,
    landingAiLabelKey: 'landing.pricing.feature.aiBuilder',
    legacyAliases: ['ENTERPRISE'],
  },
]

// Legacy plan-name → current key (matches index.ts / stripeService /
// effectivePlan LEGACY_PLAN_MAP). Documented here for the future single-source
// migration; the runtime maps are kept in their files in PR-0.
export const LEGACY_PLAN_ALIASES: Record<string, PlanKey> = { SOLO: 'STARTER', TEAM: 'PRO', ENTERPRISE: 'BUILDER' }

const byOrder = (a: PlanConfig, b: PlanConfig) => a.sortOrder - b.sortOrder

export function getPlan(key: string): PlanConfig | undefined {
  return PLAN_CATALOG.find((p) => p.key === key)
}

/** All catalog plans, ordered. */
export function getAllPlans(): PlanConfig[] {
  return [...PLAN_CATALOG].sort(byOrder)
}

/** Public, active plans (landing pricing grid). */
export function getPublicPlans(): PlanConfig[] {
  return PLAN_CATALOG.filter((p) => p.isPublic && p.status === 'active').sort(byOrder)
}

// ── Derivations consumed by the existing constants (PR-0) ────────────────────

export interface PlanLimit {
  maxProjects: number
  maxUsers: number
  aiCallsPerMonth: number
  priceMonthly: number
  storageGB: number
  extraProjectPrice: number
}

/** Backend PLAN_LIMITS shape. */
export function getPlanLimits(): Record<PlanKey, PlanLimit> {
  return Object.fromEntries(
    PLAN_CATALOG.map((p) => [
      p.key,
      {
        maxProjects: p.projectLimit,
        maxUsers: p.userLimit,
        aiCallsPerMonth: p.aiCallsPerMonth,
        priceMonthly: p.publicPriceMonthly ?? 0,
        storageGB: p.storageGB,
        extraProjectPrice: p.extraProjectPrice,
      },
    ]),
  ) as Record<PlanKey, PlanLimit>
}

/** Backend FAIR_USE_LIMITS shape. */
export function getFairUseLimits(): Record<PlanKey, { soft: number; hard: number }> {
  return Object.fromEntries(
    PLAN_CATALOG.map((p) => [p.key, { soft: p.aiSoft, hard: p.aiHard }]),
  ) as Record<PlanKey, { soft: number; hard: number }>
}

/** Numeric gating rank (STARTER < PRO < BUILDER). */
export function getPlanRank(): Record<PlanKey, number> {
  return Object.fromEntries(getAllPlans().map((p, i) => [p.key, i])) as Record<PlanKey, number>
}

/** Daily-Log export caps (for parity / future derivation). */
export function getDleLimits(): Record<PlanKey, { exportsPerMonth: number; modes: readonly ExportMode[] }> {
  return Object.fromEntries(
    PLAN_CATALOG.map((p) => [p.key, { exportsPerMonth: p.dleExportsPerMonth, modes: p.dleModes }]),
  ) as Record<PlanKey, { exportsPerMonth: number; modes: readonly ExportMode[] }>
}

// ── Frontend display derivations (preserve the exact current array shapes) ───

export interface LandingPlanRow {
  key: PlanKey
  /** Public display name (e.g. "Growth" for the BUILDER key). */
  label: string
  price: number
  priceYearly: number | null
  projects: number
  storage: string
  users: number
  ai: string
  best: boolean
}
export function getLandingPlans(): LandingPlanRow[] {
  // Self-serve pricing grid: public, active, priced plans (contact-only plans
  // render separately, not as a price card).
  return getPublicPlans()
    .filter((p) => p.publicPriceMonthly != null)
    .map((p) => ({
      key: p.key,
      label: p.displayName.en, // plan names are proper nouns — same EN/ES
      price: p.publicPriceMonthly ?? 0,
      priceYearly: p.publicPriceYearly ?? null,
      projects: p.projectLimit,
      storage: `${p.storageGB} GB`,
      users: p.userLimit,
      ai: p.landingAiLabelKey,
      best: p.recommended,
    }))
}

export interface BillingPlanRow {
  key: PlanKey
  /** Public display name (e.g. "Growth" for the BUILDER key). */
  label: string
  price: number
  priceYearly: number | null
  projects: number
  storage: number
  extra: number
  users: number
  scans: number
  popular: boolean
}
export function getBillingPlans(): BillingPlanRow[] {
  return getAllPlans()
    .filter((p) => p.status === 'active' && p.publicPriceMonthly != null)
    .map((p) => ({
      key: p.key,
      label: p.displayName.en,
      price: p.publicPriceMonthly ?? 0,
      priceYearly: p.publicPriceYearly ?? null,
      projects: p.projectLimit,
      storage: p.storageGB,
      extra: p.extraProjectPrice,
      users: p.userLimit,
      scans: p.aiCallsPerMonth,
      popular: p.recommended,
    }))
}

/** ProFeatureGate PLAN_ORDER. */
export function getProGateOrder(): Record<PlanKey, number> {
  return getPlanRank()
}
/** ProFeatureGate PLAN_PRICES (upgrade-target plans only; "$89"/"$209"). */
export function getProGatePrices(): Record<string, string> {
  return Object.fromEntries(
    getAllPlans()
      .filter((p) => p.key !== 'STARTER' && p.publicPriceMonthly != null)
      .map((p) => [p.key, `$${p.publicPriceMonthly}`]),
  )
}
